import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Container, Row } from 'react-bootstrap';
import CarouselItem from './carousel-item/CarouselItem';
import Dots from './dots/Dots';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import classnames from 'classnames';

import './ControlledCarousel.scss';

const ControlledCarousel = ({ items }) => {
    const SLIDE_CLASS = 'slideItem';
    const [listItems, setListItems] = useState(null);
    const [shouldSlide, setShouldSlide] = useState(false);
    const [direction, setDirection] = useState(null);
    const [clicked, setClicked] = useState(false);
    const [count, setCount] = useState(0);
    const tmpArray = new Array(items.length).fill(false);

    const carouselItems = useRef(null);

    let x = 0;
    let xDown = null;
    let yDown = null;
    let mobileCardPosTemp = 0;

    tmpArray[0] = true;

    const [dotHandler, setDotHandler] = useState({
        items: tmpArray,
        active: 0
    });

    const setDotsPosition = (index) => {
        let tmp = [...(dotHandler.items)];
        tmp[dotHandler.active] = false;
        tmp[index] = true;

        setDotHandler({
            items: tmp,
            active: index
        });
    };

    const chevronClassNames = classnames({
        'disabled': clicked
    });

    const directionClassNames = classnames({
        'forward': direction === 1,
        'backward': direction === -1,
        'carouselItems': true
    });

    useEffect(() => {
        setListItems(items.concat(items));
    }, [items]);

    useEffect(() => {
        const node = carouselItems.current.children[0].childNodes[0];
        if (node !== null) {
            node.addEventListener('touchstart', handleTouchStart, false);
            node.addEventListener('touchmove', handleTouchMove, false);

            return () => {
                node.removeEventListener('touchstart', handleTouchStart, false);
                node.removeEventListener('touchmove', handleTouchMove, false);
            };
        }

    }, [carouselItems.current]);

    const slide = (directionParam, forceUpdateDots) => {
        setShouldSlide(true);
        setDirection(directionParam);
        setClicked(true);

        if (count === 0) {
            setCount(1);
        }

        if (directionParam === 1 && (direction === directionParam || direction === null)) {
            const tempElement = listItems[0];

            if (count === 0) {
                setListItems(listItems.splice(0, 0));
            } else {
                setListItems(listItems.splice(0, 1));
            }

            setTimeout(() => {
                if (count !== 0) {
                    listItems.push(tempElement);
                }
                setListItems(listItems);
            }, 0);

        } else if (directionParam === -1 && (direction === directionParam || direction === null)) {
            const tempElement = listItems[listItems.length - 1];

            setListItems(listItems.splice(listItems.length - 1, 1));

            setTimeout(() => {
                listItems.unshift(tempElement);
                setListItems(listItems);
            }, 0);

        }

        setTimeout(() => {
            setClicked(false);
        }, 600);

        if (forceUpdateDots) {
            setDotsPosition(x);
        }
    };

    const slideDot = (index) => {
        const elements = document.getElementsByClassName('itemWrapper');

        Array.prototype.forEach.call(elements, (el) => {
            el.style.left = -(index * el.offsetWidth) + 'px';
        });
    };

    const onClickDot = (index, touch = false) => {
        if (touch === false) {
            if (dotHandler.active === index) {
                return;
            }
        }

        slideDot(index);
        setDotsPosition(index);
    };

    const getTouches = (evt) => evt.touches || evt.originalEvent.touches;

    const handleTouchStart = (evt) => {
        const firstTouch = getTouches(evt)[0];
        xDown = firstTouch.clientX;
        yDown = firstTouch.clientY;
    };

    const getMobileCardsToUpdateOnLeft = (indexTemp) => {
        if (mobileCardPosTemp < tmpArray.length && indexTemp < tmpArray.length - 1) {
            indexTemp -= 1;
            if (Math.abs(mobileCardPosTemp - indexTemp) > 1) {
                return indexTemp + 2;
            }
            return mobileCardPosTemp + 1;
        }
        return 0;
    };

    const getMobileCardsToUpdateOnRight = (indexTemp) => {
        if (mobileCardPosTemp >= 0 && indexTemp > 0) {
            if (Math.abs(indexTemp - mobileCardPosTemp) < tmpArray.length - 1) {
                return indexTemp - 1;
            }
            return mobileCardPosTemp - 1;
        }
        return tmpArray.length - 1;
    };

    const handleTouchMove = (evt) => {
        if ( !xDown || !yDown ) {
            return;
        }

        let xUp = evt.touches[0].clientX;
        let yUp = evt.touches[0].clientY;

        let xDiff = xDown - xUp;
        let yDiff = yDown - yUp;

        let numberRegex = /\d+/g;
        let leftStyle = (parseInt((carouselItems.current.children[0].childNodes[0].childNodes[0].style.cssText).match( numberRegex )));
        let offsetWidth = carouselItems.current.children[0].offsetWidth;
        let indexTemp = leftStyle / offsetWidth || 0;

        setTimeout(() => {
            if ( Math.abs(xDiff) > Math.abs(yDiff) ) {
                if ( xDiff > 0 ) {
                    mobileCardPosTemp = getMobileCardsToUpdateOnLeft(indexTemp);
                }
                mobileCardPosTemp = getMobileCardsToUpdateOnRight(indexTemp);

                if (mobileCardPosTemp >= 0) {
                    onClickDot(mobileCardPosTemp, true);
                }

            }
        }, 0);


        /* reset values */
        xDown = null;
        yDown = null;
    };

    const prev = (
        <div className="chevron prev"><i className={`icon-angle-down ${chevronClassNames}` } onClick={ slide.bind(null, -1, false) } ></i></div>
    );

    const next = (
        <div className="chevron next"><i className={`icon-angle-up ${chevronClassNames}` } onClick={ slide.bind(null, 1, false) } ></i></div>
    );

    const listItemsDOM =
        listItems && listItems.map((item) =>
            (<CSSTransition
                classNames={SLIDE_CLASS}
                in={shouldSlide}
                key={item?.id}
                onEntered={() => { setShouldSlide(false); }}
                timeout={500}
            >
                <CarouselItem key={item?.id}>
                    { item }
                </CarouselItem>
            </CSSTransition>)
        );

    return (
        <Container
            className='controlledCarousel'
            id="controlledCarousel"
            ref={carouselItems}
        >
            <Row>
                <TransitionGroup className={directionClassNames}>
                    { listItemsDOM }
                </TransitionGroup>
                { prev }
                { next }
                <Dots
                    items={dotHandler.items}
                    onClickDot={onClickDot}
                />
            </Row>
        </Container>
    );
};

ControlledCarousel.propTypes = {
    items: PropTypes.array.isRequired
};

export default ControlledCarousel;
